import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { Box, MenuItem, Divider, Typography, Chip } from '@mui/material';
import { ordersAdvancedSearch } from '../../../api/orders';
import Markdown from '../../../reusable-components/markdown';
import { highlightText } from '../../../reusable-components/highlight-element/highlight-element';
import { fMonthDayYearShort } from '../../../utils/formatTime';
import QuickSearchDropdown from './QuickSearchDropdown';

OrderSearchQuickDropdown.propTypes = {
  searchValue: PropTypes.string, 
  quickAdvancedFilter: PropTypes.bool,
  setQuickAdvancedFilter: PropTypes.func,
  advancedSearchBase: PropTypes.string,
  searchInputRef: PropTypes.object
}

export default function OrderSearchQuickDropdown({ searchValue, quickAdvancedFilter, setQuickAdvancedFilter, advancedSearchBase, searchInputRef }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(null);

  const searchValueTrimmed = searchValue?.trim();

  const handleSearchCall = () => {
    setLoading(true);
    const startDate = new Date(new Date().setDate(new Date().getDate() - 29)).toISOString();
    const endDate = new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toISOString();
    const fieldsToSearch = ['orderNumber', 'department', 'submittedBy', 'manager', 'facilityName', 'vendor', 'vendorItemId', 'description'];
    ordersAdvancedSearch({
      startDate,
      endDate,
      searchValue: searchValueTrimmed,
      page: 1,
      pageSize: 5,
      ...fieldsToSearch.reduce((acc, field) => ({ ...acc, [field]: true }), {})
    }).then((res) => {
      setSearchResults(res.data);
    })
    .catch(() => enqueueSnackbar("Failed to load messages", { variant: "error" }))
    .finally(() => setLoading(false));
  };

  const goToAdvancedSearch = () => navigate(`/dashboard/advanced-search?vendor=true&manager=true&facilityName=true&department=true&orderNumber=true&submittedBy=true&vendorItemId=true&description=true&searchValue=${searchValueTrimmed}&base=${advancedSearchBase}&page=1`);;
  const goToOrder = (id) => navigate(`/dashboard/orders/${id}`);

  const getRespDetails = (res) => [
    `**Department**: ${highlightText(res.department, searchValueTrimmed)}`,
    `**Facility**: ${highlightText(res.facility, searchValueTrimmed)}`,
    `**Facility Managers**: ${highlightText(res.facilityManagers?.map(m => `${m.manager} ${m.email}`).join(", "), searchValueTrimmed)}`,
    `**Submitted by**: ${highlightText(res.submittedBy, searchValueTrimmed)}`
  ];

  const SearchResultItems = searchResults?.results?.map((res, index) =>
  <>
    <MenuItem key={index} onClick={() => goToOrder(res.orderId)} sx={{ whiteSpace: 'break-spaces' }}>
      <Box sx={{ width: 575 }}>
        <Box display="flex" justifyContent="space-between">
          <Markdown sx={{ "& .MuiTypography-root": { fontWeight: "700" } }} children={`Order# ${highlightText(res.orderNumber, searchValueTrimmed)}`} />
          <Typography sx={{ ml: 1 }} variant="caption">{fMonthDayYearShort(res.dateAdded)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap'}}>
        {getRespDetails(res).map((detail) => <Box
          key={detail}
          sx={{
            mr: 0.5,
            mb: 0.5,
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            borderRadius: '5px',
            padding: '3px 5px',
          }}
        >
          <Markdown children={detail} sx={{ "& .MuiTypography-root": { fontSize: "12px" } }}/>
        </Box>)}
        </Box>
        {res?.items?.length > 0 && <Box>
          <Divider color="#c3c0c0" sx={{ my: .2 }} />
          <Box sx={{display: 'flex'}}>
            <Markdown
              sx={{ "& .MuiTypography-root": { fontSize: "small !important" } }}
              children={`**SKU**: ${highlightText(res.items[0].vendorItemId, searchValueTrimmed)} | **Description**: ${highlightText(res.items[0].description, searchValueTrimmed)} | **Vendor**: ${highlightText(res.items[0].vendor, searchValueTrimmed)}`}
            />
          </Box>
        </Box>}
      </Box>
    </MenuItem>
    <Divider color="black" sx={{ width: '95%', margin: '0 auto !important' }} />
  </>);

  return <QuickSearchDropdown
    loading={loading}
    handleSearchCall={handleSearchCall}
    goToAdvancedSearch={goToAdvancedSearch}
    searchResultItems={SearchResultItems}
    quickAdvancedFilter={quickAdvancedFilter}
    setQuickAdvancedFilter={setQuickAdvancedFilter}
    searchValue={searchValueTrimmed}
    setSearchResults={setSearchResults}
    searchResults={searchResults}
    searchInputRef={searchInputRef}
  />
}